import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import SaveRecommendedHolidayModal from './SaveRecommendedHolidayModal.js';
import HolidayRow from './HolidayRow.js';
import withLocalization from '~/hoc/withLocalization';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';
import Select from '../../elements/Select.js';
import Button from '../../components/CustomButton/CustomButton.jsx';

//Utils
import { generateYearOptions } from '../../utils/timeCalc';
import { transformHolidayData } from '../../utils/commonMethods.js';

// Assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'selected', label: 'Active' },
    { value: 'unselected', label: 'InActive' },
];

class HolidaySettingsModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            activeTab: new Date().getFullYear().toString(),
            allHolidaysData: {},
            loading: false,
            isTabRendering: false,
            hasUnsavedChanges: false,
            searchTerm: '',
            showRecommendedModal: false,
            recommendedHoliday: null,
            recommendedMasterHolidayId: null,
        };

        this.payrollOptions = null;
        this.years = generateYearOptions();
        this.tabRenderTimeout = null;
        this.filterTimeout = null;
    }

    componentDidMount() {
        if (this.props.isOpen) {
            this.loadAllHolidays();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isOpen && this.props.isOpen) {
            this.loadAllHolidays();
        }
    }

    componentWillUnmount() {
        if (this.tabRenderTimeout) clearTimeout(this.tabRenderTimeout);
        if (this.filterTimeout) clearTimeout(this.filterTimeout);
    }

    handleSearchChange = (e) => {
        this.setState({ searchTerm: e.target.value });
    };

    handleFilterChange = async (option) => {
        const startTime = Date.now();
        this.setState({ isTabRendering: true, filterType: option });
        await new Promise((resolve) => requestAnimationFrame(resolve));
        const elapsed = Date.now() - startTime;
        if (elapsed < 100) {
            this.filterTimeout = await new Promise((resolve) => setTimeout(resolve, 100 - elapsed));
        }
        this.setState({ isTabRendering: false });
    };

    getFilteredHolidays = () => {
        const { searchTerm, filterType } = this.state;
        let currentHolidays = this.getCurrentHolidays();
        if (searchTerm.trim()) {
            currentHolidays = currentHolidays.filter((holiday) =>
                holiday.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (filterType?.value === 'selected') {
            currentHolidays = currentHolidays.filter((holiday) => holiday.isSelected);
        } else if (filterType?.value === 'unselected') {
            currentHolidays = currentHolidays.filter((holiday) => !holiday.isSelected);
        }
        return currentHolidays;
    };

    loadAllHolidays = async () => {
        const { flag, clientStore, t, commonStore } = this.props;
        if (!flag) return;

        this.setState({ loading: true });

        try {
            const response = await clientStore.getHolidays({ country: flag });
            const transformedData = {};

            this.years.forEach((year) => {
                const yearData = response?.data?.[year];
                transformedData[year] = {
                    holidays: yearData?.holidays ? transformHolidayData(yearData.holidays, true) : [],
                    masterHolidayId: yearData?.masterHolidayId || null,
                };
            });

            this.setState({ allHolidaysData: transformedData });
        } catch (error) {
            console.error('Error fetching holidays:', error);
            commonStore.addNotification(t('Failed to fetch holidays. Please try again.'), null, 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    getPayrollOptions = () => {
        const { commonStore } = this.props;
        if (!this.payrollOptions) {
            this.payrollOptions =
                commonStore.config?.client?.data?.extraPayments
                    ?.filter((payment) => payment.fullDay && payment.code !== 'weekends' && payment.isActive)
                    ?.map((payment) => ({
                        value: payment.code,
                        label: `${payment.name} (${payment.payrollWageType?.label || payment.code})`,
                    })) || [];
        }
        return this.payrollOptions;
    };

    getCurrentHolidays = () => {
        const { activeTab, allHolidaysData } = this.state;
        return allHolidaysData[activeTab]?.holidays || [];
    };

    handleTabChange = (tab) => {
        const { t } = this.props;
        if (this.state.activeTab === tab) return;

        if (this.state.hasUnsavedChanges) {
            confirmAlert({
                title: t('Do you want to save the changes') + '?',
                message: t("Changes are not saved yet and hence won't reflect, do you wish to save them"),
                buttons: [
                    {
                        label: t('Save'),
                        onClick: async () => {
                            this.setState({ isTabRendering: true });
                            await this.handleSave();
                            this.setState({ activeTab: tab, hasUnsavedChanges: false, isTabRendering: false });
                        },
                    },
                    {
                        label: t('Discard'),
                        onClick: () => {
                            this.setState({ isTabRendering: true }, async () => {
                                await this.loadAllHolidays();
                                this.setState({ activeTab: tab, hasUnsavedChanges: false, isTabRendering: false });
                            });
                        },
                    },
                ],
            });
        } else {
            if (this.tabRenderTimeout) clearTimeout(this.tabRenderTimeout);
            this.setState({ activeTab: tab, isTabRendering: true });
            this.tabRenderTimeout = setTimeout(() => {
                this.setState({ isTabRendering: false });
            }, 50);
        }
    };

    handleFetchHolidays = async () => {
        const { flag, clientStore, t, commonStore } = this.props;
        const { activeTab } = this.state;
        if (!flag) return;

        this.setState({ loading: true });

        try {
            await clientStore.getHolidayList({ country: flag, year: activeTab });
            await this.loadAllHolidays();
            commonStore.addNotification(t('Holiday data fetched successfully'), null, 'success');
        } catch (error) {
            commonStore.addNotification(t('Failed to fetch holidays. Please try again'), null, 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    updateHolidayData = (holidayId, updater) => {
        const { activeTab } = this.state;

        this.setState((prevState) => {
            const currentData = prevState.allHolidaysData[activeTab];
            if (!currentData) return prevState;

            const updatedHolidays = currentData.holidays.map((holiday) => {
                return holiday.id === holidayId ? { ...holiday, ...updater(holiday) } : holiday;
            });

            return {
                allHolidaysData: {
                    ...prevState.allHolidaysData,
                    [activeTab]: { ...currentData, holidays: updatedHolidays },
                },
            };
        });
    };

    checkDateConflict = (holidayId, isSelecting) => {
        const { activeTab, allHolidaysData } = this.state;
        const currentHolidays = allHolidaysData[activeTab]?.holidays || [];
        const targetHoliday = currentHolidays.find(h => h.id === holidayId);
        if (!targetHoliday) return null;
        if (!isSelecting) return null;
        const conflictingHoliday = currentHolidays.find(h =>
            h.id !== holidayId &&
            h.date === targetHoliday.date &&
            h.isSelected
        );
        return conflictingHoliday;
    };

    handleHolidaySelect = (holidayId, checked) => {
        const { t, commonStore } = this.props;
        if (checked) {
            const conflictingHoliday = this.checkDateConflict(holidayId, true);

            if (conflictingHoliday) {
                commonStore.addNotification(t('This date is already marked as a holiday.') + `: ${conflictingHoliday.custom_name || conflictingHoliday.name}`, null, 'error');
                return;
            }
        }
        this.updateHolidayData(holidayId, () => ({ isSelected: checked, accountedInVacation: checked ? true : false }));
        this.setState({ hasUnsavedChanges: true });
    };

    handleCustomNameChange = (holidayId, value) => {
        this.updateHolidayData(holidayId, () => ({ custom_name: value }));
        this.setState({ hasUnsavedChanges: true });
    };

    handlePayrollChange = (holidayId, value) => {
        const { commonStore } = this.props;
        const selectedPayment = commonStore.config?.client?.data?.extraPayments?.find(
            (payment) => payment.code === value
        );

        this.updateHolidayData(holidayId, () => ({ additionalPayment: selectedPayment }));
        this.setState({ hasUnsavedChanges: true });
    };

    handleCountInChange = (holidayId, checked, type) => {
        this.updateHolidayData(holidayId, () => ({ [type]: checked }));
        this.setState({ hasUnsavedChanges: true });
    };

    handleSave = async () => {
        const { clientStore, flag, t, commonStore } = this.props;
        const { activeTab, allHolidaysData } = this.state;

        const currentYearData = allHolidaysData[activeTab] || {};
        const selectedHolidays = (currentYearData.holidays || []).filter((holiday) => holiday.isSelected);

        if (selectedHolidays.length === 0) {
            commonStore.addNotification(t('Please select at least one holiday to continue.'), null, 'warning');
            return;
        }

        for (let holiday of selectedHolidays) {
            if (!holiday.custom_name || holiday.custom_name.trim() === '') {
                commonStore.addNotification(t('Holiday display name cannot be empty'), null, 'error');
                return;
            }
        }
        const displayNames = selectedHolidays.map((h) => h.custom_name.trim());
        const duplicateName = displayNames.find((name, index) => displayNames.indexOf(name) !== index);
        if (duplicateName) {
            commonStore.addNotification(t('Holiday display names must be unique among selected holidays') + `:${duplicateName}`, null, 'error');
            return;
        }

        const masterHolidayId = currentYearData.masterHolidayId || null;

        this.setState({ loading: true });

        try {
            const data = { country: flag, year: activeTab, holidays: selectedHolidays, masterHolidayId };
            await clientStore.saveHolidays(data);
            commonStore.addNotification(t('Saved'), null, 'success');
            this.setState({ hasUnsavedChanges: false });
            await this.loadAllHolidays();
            await commonStore.setAppLoaded(false);
        } catch (error) {
            let errorMessage = t('Failed to save holidays. Please try again.');
            if (error.response?.data?.message)
                errorMessage = error.response.data.message;
            commonStore.addNotification(errorMessage, null, 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    handleRecommendedModal = () => {
        this.setState({ showRecommendedModal: false });
    };

    fetchRecommendedHolidaysData = async () => {
        const { clientStore, flag } = this.props;
        const { yearlyHoliday } = await clientStore.getRecommendedHolidays({ country: flag, onlyRecommendedData: true, year: this.state.activeTab });
        this.setState({
            recommendedHoliday: yearlyHoliday?.data?.holidays || null,
            recommendedMasterHolidayId: yearlyHoliday?.data?.masterHolidayId || null,
        });
    };

    saveRecommendedHoliday = async () => {
        const { clientStore, flag, t, commonStore } = this.props;
        const { activeTab, recommendedHoliday, recommendedMasterHolidayId, allHolidaysData } = this.state;

        const existingHolidays = allHolidaysData[activeTab]?.holidays || [];

        const adminSelected = existingHolidays.filter((h) => h.isSelected);
        const adminSelectedDates = new Set(adminSelected.map((h) => h.date));

        const newFromRecommended = recommendedHoliday
            .filter((rec) => !adminSelectedDates.has(rec.date))
            .map((rec) => ({ ...rec, isSelected: true }));

        const merged = [...adminSelected, ...newFromRecommended];

        const data = { country: flag, year: activeTab, holidays: merged, masterHolidayId: recommendedMasterHolidayId };
        await clientStore.saveHolidays(data);
        commonStore.addNotification(t('Saved'), null, 'success');
        await this.loadAllHolidays();
        await commonStore.setAppLoaded(false);
        this.handleRecommendedModal();
    };

    openRecommendationModal = () => {
        this.setState({ showRecommendedModal: true });
    };

    openDeleteHolidayModal = async () => {
        const { clientStore, commonStore, t } = this.props;
        confirmAlert({
            title: this.props.t('Delete Holidays'),
            message: t(`Are you sure you want to delete enabled holidays for year ` + this.state.activeTab),
            buttons: [
                {
                    label: this.props.t('Yes'),
                    onClick: async () => {
                        const data = {
                            country: this.props.flag,
                            year: this.state.activeTab,
                        };
                        const deleteHoliday = await clientStore.deleteHoliday(data);
                        commonStore.addNotification(this.props.t(deleteHoliday?.message || 'Successfully deleted'), null, 'success');
                        await this.loadAllHolidays();
                        await commonStore.setAppLoaded(false);
                    },
                },
                {
                    label: this.props.t('No'),
                    onClick: () => { },
                },
            ],
        });
    };

    render() {
        const { isOpen, toggle, onCancel, t } = this.props;
        const { activeTab, loading, isTabRendering, filterType } = this.state;

        const currentHolidays = this.getFilteredHolidays();
        const payrollOptions = this.getPayrollOptions();
        const hasDataForTab = currentHolidays.length > 0;
        const allHolidays = this.getCurrentHolidays();

        const showContent = !loading && !isTabRendering && hasDataForTab;
        const showFilteredEmptyState = !loading && !isTabRendering && allHolidays.length > 0 && !hasDataForTab;
        const showEmptyState = !loading && !isTabRendering && allHolidays.length === 0;
        const showSpinner = loading || isTabRendering;

        return (
            <>
                <Modal show={isOpen} onHide={toggle} size="xl">
                    <Modal.Header>
                        <Modal.Title>{t('Configure Holidays')}</Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            onClick={() => toggle()}
                            alt={'cross-Icon'}
                        />
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col sm={12}>
                                <Row className="d-flex justify-content-between align-items-center">
                                    <Col xs="auto">
                                        <Nav tabs>
                                            {this.years.map((year) => (
                                                <NavItem key={year}>
                                                    <NavLink
                                                        className={activeTab === year ? 'active' : ''}
                                                        onClick={() => this.handleTabChange(year)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {year}
                                                    </NavLink>
                                                </NavItem>
                                            ))}
                                        </Nav>
                                    </Col>
                                    <Col xs="auto">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="tooltip-top">
                                                    {t('Check recommended holidays for the selected year')}
                                                </Tooltip>
                                            }
                                        >
                                            <Button fill onClick={() => this.setState({ showRecommendedModal: true })}>
                                                {t('Recommended Holidays')}
                                            </Button>
                                        </OverlayTrigger>
                                        <Button ms2 fill onClick={this.openDeleteHolidayModal}>
                                            {t('Remove All Holiday')}
                                        </Button>
                                    </Col>
                                </Row>
                                <div style={{ fontSize: '12px' }} className="mb-1">
                                    {t('Customize your holiday settings by enabling holidays, editing holiday names, configuring payroll supplements, and managing holiday vacation/absence day calculations.')}
                                </div>
                                <Row className="mb-2">
                                    <Col md={4}>
                                        <div className="small text-muted">
                                            <div style={{ fontSize: '12px' }}>
                                                • {t('Settings are year-specific and do not affect other years')}
                                            </div>
                                            <div style={{ fontSize: '12px' }}>
                                                • {t('Past holidays cannot be modified')}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <Select
                                            options={filterOptions}
                                            value={this.state.filterType}
                                            onChange={this.handleFilterChange}
                                            placeholder={t('Filter Holidays')}
                                            isSearchable={false}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <div className="conversation-search">
                                            <input
                                                type="text"
                                                className="conversation-search-input"
                                                placeholder={t('Search holidays by name')}
                                                value={this.state.searchTerm}
                                                onChange={this.handleSearchChange}
                                                style={{ height: '20px' }}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <TabContent activeTab={activeTab}>
                                    {this.years.map((year) => (
                                        <TabPane tabId={year} key={year} className="p-0">
                                            <Row>
                                                <Col sm={12}>
                                                    {showSpinner && activeTab === year && <LoadingSpinner />}
                                                    {showContent && activeTab === year && (
                                                        <div className="projectMemberForm">
                                                            <table className="table table-hover projectMembersFormTable">
                                                                <thead>
                                                                    <tr className="table-container-header text-center">
                                                                        <th style={{ width: '8%', textAlign: 'center' }}>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id="select-holiday-tooltip">
                                                                                        {t('Enable to include holiday in your system')}
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span>
                                                                                    {t('Enable')}{' '}
                                                                                    <i className="fa fa-info-circle" />
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </th>
                                                                        <th style={{ width: '18%', textAlign: 'left' }}>{t('Holiday Name')}</th>
                                                                        <th style={{ width: '12%', textAlign: 'left' }}>{t('Date')} </th>
                                                                        <th style={{ width: '10%', textAlign: 'left' }}> {t('Type')}</th>
                                                                        <th style={{ width: '18%' }}>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id="display-name-tooltip">
                                                                                        {t('You can set a custom display name for the holiday')}
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span>
                                                                                    {t('Display Name')}{' '}
                                                                                    <i className="fa fa-info-circle" />
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </th>
                                                                        <th style={{ width: '18%' }}>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id="payroll-supplement-tooltip">
                                                                                        {t('Choose a payroll supplement for employees working on this holiday')}
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span>
                                                                                    {t('Payroll Supplement')}{' '}
                                                                                    <i className="fa fa-info-circle" />
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </th>
                                                                        <th style={{ width: '8%', textAlign: 'center' }}>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id="vacation-tooltip">
                                                                                        {t('If enabled the default holidays will not be considered while calculating the total vacation days')}
                                                                                    </Tooltip>
                                                                                }
                                                                            >
                                                                                <span>
                                                                                    {t('Vacation')}{' '}
                                                                                    <i className="fa fa-info-circle" />
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </th>
                                                                        <th style={{ width: '10%', textAlign: 'center' }}>
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={
                                                                                    <Tooltip id="absence-tooltip">
                                                                                        {t('If enabled the default holidays will not be considered while calculating the total absence days')}
                                                                                    </Tooltip>}
                                                                            >
                                                                                <span>
                                                                                    {t('Absence')}{' '}
                                                                                    <i className="fa fa-info-circle" />
                                                                                </span>
                                                                            </OverlayTrigger>
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {currentHolidays.length > 0 &&
                                                                        currentHolidays.map((holiday) => (
                                                                            <HolidayRow
                                                                                key={holiday.id}
                                                                                holiday={holiday}
                                                                                payrollOptions={payrollOptions}
                                                                                onHolidaySelect={this.handleHolidaySelect}
                                                                                onCustomNameChange={this.handleCustomNameChange}
                                                                                onPayrollChange={this.handlePayrollChange}
                                                                                onCountInChange={this.handleCountInChange}
                                                                                showAdvancedColumns={true}
                                                                                t={t}
                                                                            />
                                                                        ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}

                                                    {showEmptyState && activeTab === year && (
                                                        <div className="text-center mt-3">
                                                            <Button
                                                                color="primary"
                                                                onClick={this.handleFetchHolidays}
                                                                disabled={loading}
                                                            >
                                                                {loading ? <LoadingSpinner /> : t('Fetch Holiday')}
                                                            </Button>
                                                        </div>
                                                    )}

                                                    {showFilteredEmptyState && activeTab === year && (
                                                        <div className="text-center mt-3">
                                                            <p className="text-muted">
                                                                {t('No data found')}
                                                            </p>
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </TabPane>
                                    ))}
                                </TabContent>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button color="primary" onClick={this.handleSave} disabled={loading || isTabRendering} fill>
                            {loading ? (
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            ) : (t('Save Holidays'))}
                        </Button>
                        <Button color="secondary" onClick={onCancel} disabled={loading}>{t('Cancel')}</Button>
                    </Modal.Footer>
                </Modal>
                {this.state.showRecommendedModal && (
                    <SaveRecommendedHolidayModal
                        isOpen={this.state.showRecommendedModal}
                        onClose={this.handleRecommendedModal}
                        activeTab={activeTab}
                        flag={this.props.flag}
                        recommendedHoliday={this.state.recommendedHoliday}
                        onDataFetch={this.fetchRecommendedHolidaysData}
                        onSave={this.saveRecommendedHoliday}
                    />
                )}
            </>
        );
    }
}

export default inject('clientStore', 'commonStore')(withLocalization(observer(HolidaySettingsModal)));
